import * as React from 'react'
import {Box, Typography, Button, LinearProgress, Tooltip} from '@mui/material'
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined'
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'

import {sectionCardSx, alertStripSx, primaryBtnSx, secondaryBtnSx} from '../../../../styles/patterns'
import {FONT, SIZE, WEIGHT} from '../../../../styles/foundation/typography'
import {NAVY, SUBTLE, PRIMARY_BLUE, SUCCESS, WARNING, ERROR, INFO, MUTED} from '../../../../styles/foundation/colors'
import {GAP} from '../../../../styles/foundation/spacing'

import {int, kg, pct} from '../format'
import {MERCHANT} from '../data/noberoFreeze'
import {catalogueCounters} from './catalogueMath'
import useCatalogueState from './useCatalogueState'
import CatalogueFilters from './CatalogueFilters'
import CatalogueTable from './CatalogueTable'
import MappingHistory from './MappingHistory'
import MergeSuggestions from './MergeSuggestions'
import MergeDrawer from './MergeDrawer'
import UploadDrawer from './UploadDrawer'
import ReviewSkuDrawer from './ReviewSkuDrawer'
import FixCodeDrawer from './FixCodeDrawer'
import {downloadTemplate} from './productTemplate'

const BAD_CODE_VISIBLE = 5

/**
 * Catalogue's four figures as stat cards for the strip Package master renders
 * ABOVE its tab bar (see freeze-v2/index.js). They used to be a tile row inside
 * this page; the strip is now the sub-module's header, so the row moved up and
 * only the data comes from here.
 *
 * A builder rather than a component: the container needs the numbers, not
 * markup, and the derivation stays where it already lives — `catalogueCounters`
 * in catalogueMath.js, the same function this page's own banners count from, so
 * a card and the banner beneath it cannot quote two different figures.
 *
 * Colours are conditional on purpose: a figure that is zero because there is
 * nothing wrong reads green, and the same card reads amber or red the moment
 * there is something to act on. `note` carries the denominator or the caveat
 * that a two-word uppercase label cannot.
 */
export function catalogueStatCards(skus) {
  const c = catalogueCounters(skus)
  return [
    {
      id: 'total',
      value: c.total,
      label: 'Catalogue SKUs',
      note: `In the catalogue, of ${int(MERCHANT.totals.skus)} shipped`,
      color: PRIMARY_BLUE,
    },
    {
      id: 'autoPending',
      value: c.autoPending,
      label: 'Awaiting review',
      note: 'Auto-added, awaiting review',
      color: c.autoPending > 0 ? WARNING : SUCCESS,
    },
    {
      id: 'badCodes',
      value: c.badCodes.length,
      label: 'Unusable codes',
      note: 'Codes unusable as they stand',
      color: c.badCodes.length > 0 ? ERROR : MUTED,
    },
    {
      id: 'mapped',
      value: c.mapped,
      label: 'Mapped to a mother',
      note: 'Codes mapped to a mother SKU',
      color: INFO,
    },
  ]
}

/**
 * The Catalogue page — banners, filters, table, selection bar, suggested
 * merges, the merge drawer and mapping history (Tasks 2-5). Its figures are
 * `catalogueStatCards` above, drawn by the sub-module's own header strip.
 * `props` is whatever index.js threads down from the one shared
 * `useFreezeStore()` instance (skus, mapLog, packs, catalogueSyncing, actor,
 * actions, mergeCandidates, plus the sibling slices other sections read, and
 * `focusFallbackRef` — see the merge-drawer focus note below) — see
 * useCatalogueState.js for exactly what this page consumes.
 *
 * Every placeholder this page used to raise ("ships in a later step") has
 * now been replaced: Merge by `MergeSuggestions`/`MergeDrawer` (Task 5), the
 * Sample CSV / Upload catalogue controls by `productTemplate.js`'s
 * `downloadTemplate()` and `UploadDrawer` (Task 6), and Review / Fix code by
 * `ReviewSkuDrawer`/`FixCodeDrawer` below (Task 7) — the last two. There is
 * no `notReady` helper left in this file; every control now completes the
 * action it names.
 */
export default function CataloguePage(props) {
  const cat = useCatalogueState(props)
  const {mergeCandidates, focusFallbackRef, subcats, onNavigate} = props
  const {
    state, rows, roll, filters, setFilter, resetFilters,
    selection, selected, hiddenSelected, blocked, canMerge,
    toggleSelect, selectAllOnPage, clearSelection,
    page, setPage, perPage, counters, subOptions, actions, packs, syncing, merchant,
  } = cat

  const [showAllBad, setShowAllBad] = React.useState(false)

  // Merge drawer — Task 5. `mergeCodes` is null when the drawer is closed,
  // else the codes it was opened with (either the selection-bar's ticked
  // rows, or a two-code suggestion from MergeSuggestions). Both openers
  // share this one piece of state rather than each holding their own, so
  // there is only ever one drawer instance and one source of truth for what
  // it is showing.
  const [mergeCodes, setMergeCodes] = React.useState(null)

  // Submitting clears the selection (see useCatalogueState's own `merge`),
  // which is what makes the "Merge under a mother SKU" button — the trigger
  // that opened this drawer — disappear before MUI's own focus-restore can
  // put focus back on it; the same happens to a MergeSuggestions row's
  // "Review merge" button once its candidate flips to `done`. When that
  // leaves focus on <body>, fall back to the active rail tab instead
  // (StepRail's own `focusActive()`, threaded down from index.js as
  // `focusFallbackRef`) rather than letting keyboard focus vanish. Checked
  // on the next frame so the DOM has actually reflected the vanished
  // trigger by the time it runs.
  const closeMergeDrawer = React.useCallback(() => {
    setMergeCodes(null)
    if (typeof window === 'undefined' || typeof document === 'undefined') return
    window.requestAnimationFrame(() => {
      if (document.activeElement === document.body && focusFallbackRef && focusFallbackRef.current) {
        focusFallbackRef.current.focusActive()
      }
    })
  }, [focusFallbackRef])

  const handleMerge = React.useCallback((mother, childCodes) => {
    actions.merge(mother, childCodes)
    closeMergeDrawer()
  }, [actions, closeMergeDrawer])

  // Upload drawer — Task 6. Own open/close boolean rather than the merge
  // drawer's null-vs-codes shape: there is exactly one trigger ("Upload
  // catalogue" above) and no second opener to share state with. The
  // trigger button itself never disappears (unlike "Merge under a mother
  // SKU", which vanishes once a merge clears the selection), so MUI's own
  // focus-restore should already return focus there on close — but this
  // reuses the SAME focusFallbackRef fallback as closeMergeDrawer above
  // rather than assuming that, per the task brief's own instruction not to
  // invent a second focus-recovery mechanism.
  const [uploadOpen, setUploadOpen] = React.useState(false)

  const closeUploadDrawer = React.useCallback(() => {
    setUploadOpen(false)
    if (typeof window === 'undefined' || typeof document === 'undefined') return
    window.requestAnimationFrame(() => {
      if (document.activeElement === document.body && focusFallbackRef && focusFallbackRef.current) {
        focusFallbackRef.current.focusActive()
      }
    })
  }, [focusFallbackRef])

  const handleImport = React.useCallback((importedRows, fileName) => {
    actions.importRows(importedRows, fileName)
    closeUploadDrawer()
  }, [actions, closeUploadDrawer])

  const byCode = React.useMemo(() => new Map(state.skus.map((s) => [s.sku, s])), [state.skus])
  const canMarkReviewed = selected.some((code) => {
    const s = byCode.get(code)
    return !!s && s.source === 'auto' && !s.reviewed && !s.dq
  })

  // Review / fix-code drawers — Task 7. Each holds only the CODE that opened
  // it, not the row object the table/banner handed the click — the live sku
  // is re-resolved off `byCode` every render (below), exactly like MergeDrawer
  // re-resolves `codes` off `skus` rather than trusting a snapshot that could
  // go stale the moment a save actually lands.
  const [reviewCode, setReviewCode] = React.useState(null)
  const [fixCode, setFixCode] = React.useState(null)
  const reviewingSku = reviewCode ? byCode.get(reviewCode) : null
  const fixingSku = fixCode ? byCode.get(fixCode) : null

  // Same focus-recovery fallback as closeMergeDrawer/closeUploadDrawer above
  // — reused, not reinvented, per the task brief's own instruction. A
  // review's or a code fix's own trigger (the table row's "Review"/"Fix
  // code" button, or the bad-code banner's "Assign a real code") can vanish
  // the instant the save lands: reviewing clears `needsReview`, and fixing a
  // code drops the row out of the bad-codes banner entirely.
  const closeReviewDrawer = React.useCallback(() => {
    setReviewCode(null)
    if (typeof window === 'undefined' || typeof document === 'undefined') return
    window.requestAnimationFrame(() => {
      if (document.activeElement === document.body && focusFallbackRef && focusFallbackRef.current) {
        focusFallbackRef.current.focusActive()
      }
    })
  }, [focusFallbackRef])

  const closeFixCodeDrawer = React.useCallback(() => {
    setFixCode(null)
    if (typeof window === 'undefined' || typeof document === 'undefined') return
    window.requestAnimationFrame(() => {
      if (document.activeElement === document.body && focusFallbackRef && focusFallbackRef.current) {
        focusFallbackRef.current.focusActive()
      }
    })
  }, [focusFallbackRef])

  const handleSaveReview = React.useCallback((code, patch) => {
    actions.saveReview(code, patch)
    closeReviewDrawer()
  }, [actions, closeReviewDrawer])

  const handleSaveCode = React.useCallback((code, nextCode) => {
    actions.saveCode(code, nextCode)
    closeFixCodeDrawer()
  }, [actions, closeFixCodeDrawer])

  const mergeDisabledReason = selected.length < 2
    ? 'Select at least two SKUs to merge.'
    : blocked.length > 0
      ? 'Some selected codes already report to a mother SKU.'
      : ''

  const badVisible = counters.badCodes.length > BAD_CODE_VISIBLE && !showAllBad
    ? counters.badCodes.slice(0, BAD_CODE_VISIBLE)
    : counters.badCodes

  return (
    <Box sx={{display: 'grid', gap: GAP.lg}}>
      {/* The page's controls. There is no heading above them any more: the app
          header now names the sub-module and the tab strip names this leaf, so a
          third "Catalogue" — plus two lines restating what a catalogue is — was
          the same fact three times over. The actions are what a merchant came
          here to press, so they are all that is left, and they keep the
          top-right corner they already occupied. */}
      <Box sx={{display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 1}}>
        <Button
          sx={secondaryBtnSx}
          startIcon={<DownloadOutlinedIcon sx={{fontSize: 16}} />}
          onClick={downloadTemplate}
        >
          Sample CSV
        </Button>
        <Button
          sx={secondaryBtnSx}
          startIcon={<SyncOutlinedIcon sx={{fontSize: 16}} />}
          disabled={syncing}
          onClick={actions.pullFromOms}
        >
          {syncing ? 'Pulling from OMS/WMS' : 'Pull from OMS/WMS'}
        </Button>
        <Button
          sx={primaryBtnSx}
          startIcon={<CloudUploadOutlinedIcon sx={{fontSize: 16}} />}
          onClick={() => setUploadOpen(true)}
        >
          Upload catalogue
        </Button>
      </Box>

      {/* Permanent — no EDIT on this page is ever saved. The catalogue itself is the merchant's own. */}
      <Box sx={{...alertStripSx(INFO), alignItems: 'flex-start'}}>
        <InfoOutlinedIcon sx={{fontSize: 16, mt: '2px'}} />
        <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, lineHeight: 1.6, fontWeight: WEIGHT.regular}}>
          These are your own products, read live. Changes made here are not: merges, imports, renames and
          reviews live only in this browser tab and are gone when you reload.
        </Typography>
      </Box>

      {/* Syncing banner */}
      {syncing && (
        <Box sx={{display: 'grid', gap: 0.5}}>
          <Box sx={alertStripSx(INFO)}>
            <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs}}>
              Reading the SKU master from your OMS/WMS connector.
            </Typography>
          </Box>
          <LinearProgress sx={{borderRadius: 1}} />
        </Box>
      )}

      {/* Unusable product codes */}
      {counters.badCodes.length > 0 && (
        <Box sx={sectionCardSx}>
          <Box sx={{...alertStripSx(WARNING), alignItems: 'flex-start', mb: 1.5}}>
            <WarningAmberOutlinedIcon sx={{fontSize: 16, mt: '2px'}} />
            <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, lineHeight: 1.6}}>
              {int(counters.badCodes.length)} product codes cannot be used. These shipments reached Prozo with
              a placeholder where the code should be, so there is nothing to attach a frozen weight to. This
              is what the source data says, not a gap in the panel.
            </Typography>
          </Box>
          <Box sx={{display: 'grid', gap: 1}}>
            {badVisible.map((s) => (
              <Box
                key={s.sku}
                sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap',
                  py: 0.75, borderBottom: `1px solid ${MUTED}22`,
                }}
              >
                <Box sx={{minWidth: 0}}>
                  <Typography sx={{fontFamily: 'monospace', fontWeight: WEIGHT.semibold, fontSize: SIZE.xs, color: NAVY, wordBreak: 'break-all'}}>
                    {s.sku}
                  </Typography>
                  <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE}}>
                    {int(s.ships)} shipments, most probable weight {kg(s.mpw)}, spread {pct(s.spread)}
                  </Typography>
                </Box>
                <Button
                  size='small'
                  sx={secondaryBtnSx}
                  onClick={() => setFixCode(s.sku)}
                >
                  Assign a real code
                </Button>
              </Box>
            ))}
          </Box>
          {counters.badCodes.length > BAD_CODE_VISIBLE && (
            <Button
              size='small'
              onClick={() => setShowAllBad((v) => !v)}
              sx={{mt: 1, fontFamily: FONT, fontSize: SIZE['2xs'], textTransform: 'none', color: PRIMARY_BLUE}}
            >
              {showAllBad ? 'Show fewer' : `Show all ${int(counters.badCodes.length)}`}
            </Button>
          )}
        </Box>
      )}

      {/* Auto-added, awaiting review */}
      {counters.autoPending > 0 && (
        <Box sx={{...alertStripSx(WARNING), alignItems: 'flex-start', justifyContent: 'space-between'}}>
          <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 1}}>
            <WarningAmberOutlinedIcon sx={{fontSize: 16, mt: '2px'}} />
            <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, lineHeight: 1.6}}>
              {int(counters.autoPending)} SKUs were added automatically because they shipped without being in
              the catalogue. They keep shipping, but each one needs a sub-category and a packing material
              before it can be frozen.
            </Typography>
          </Box>
          <Button
            size='small'
            sx={{...secondaryBtnSx, flexShrink: 0}}
            onClick={() => {
              setFilter('source', 'auto')
              setFilter('sub', 'all')
              setFilter('q', '')
            }}
          >
            Show them
          </Button>
        </Box>
      )}

      {/* Suggested mother-child merges — Task 5. Opens the same drawer and
          shares the same `mergeCodes` state as the selection bar's own
          Merge button below. */}
      <MergeSuggestions
        skus={state.skus}
        candidates={mergeCandidates}
        onReviewMerge={(codes) => setMergeCodes(codes)}
      />

      {/* Table card: filters, the selection action bar, then the table. */}
      <Box sx={{display: 'grid', gap: GAP.sm}}>
        <CatalogueFilters
          filters={filters}
          onChange={setFilter}
          onReset={resetFilters}
          shown={rows.length}
          total={counters.total}
          subOptions={subOptions}
        />

        {selected.length > 0 && (
          <Box sx={{...alertStripSx(PRIMARY_BLUE), borderRadius: 0, justifyContent: 'space-between', flexWrap: 'wrap'}}>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap'}}>
              <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold}}>
                {int(selected.length)} selected
                {hiddenSelected.length > 0 && (
                  <Tooltip title={`Hidden: ${hiddenSelected.join(', ')}`}>
                    <Box component='span' tabIndex={0} sx={{fontWeight: WEIGHT.regular, cursor: 'help'}}>
                      {' '}({int(hiddenSelected.length)} not visible under current filters)
                    </Box>
                  </Tooltip>
                )}
              </Typography>
              {blocked.length > 0 && (
                <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE}}>
                  {int(blocked.length)} of them are already mapped to a mother SKU.
                </Typography>
              )}
              {hiddenSelected.length > 0 && (
                <Button
                  size='small'
                  onClick={resetFilters}
                  sx={{fontFamily: FONT, fontSize: SIZE['2xs'], textTransform: 'none', color: PRIMARY_BLUE}}
                >
                  Clear filters to show them
                </Button>
              )}
            </Box>
            <Box sx={{display: 'flex', gap: 1, alignItems: 'center'}}>
              {canMarkReviewed && (
                <Button size='small' sx={secondaryBtnSx} onClick={() => actions.markReviewed(selected)}>
                  Mark reviewed
                </Button>
              )}
              <Tooltip title={canMerge ? '' : mergeDisabledReason}>
                <span>
                  <Button
                    size='small'
                    sx={primaryBtnSx}
                    disabled={!canMerge}
                    onClick={() => setMergeCodes(selected)}
                  >
                    Merge under a mother SKU
                  </Button>
                </span>
              </Tooltip>
              <Button
                size='small'
                onClick={clearSelection}
                sx={{fontFamily: FONT, fontSize: SIZE['2xs'], textTransform: 'none', color: SUBTLE}}
              >
                Clear selection
              </Button>
            </Box>
          </Box>
        )}

        <CatalogueTable
          rows={rows}
          roll={roll}
          packs={packs}
          page={page}
          perPage={perPage}
          onPageChange={setPage}
          selection={selection}
          onToggleSelect={toggleSelect}
          onSelectAllOnPage={selectAllOnPage}
          onFixCode={(row) => setFixCode(row.sku)}
          onReview={(row) => setReviewCode(row.sku)}
          onClearFilters={resetFilters}
        />
      </Box>

      <MappingHistory log={state.mapLog} roll={roll} skus={state.skus} onUndo={actions.undoMerge} />

      <MergeDrawer
        open={mergeCodes !== null}
        skus={state.skus}
        codes={mergeCodes || []}
        onClose={closeMergeDrawer}
        onMerge={handleMerge}
      />

      <UploadDrawer
        open={uploadOpen}
        skus={state.skus}
        packs={packs}
        merchantName={merchant.name}
        onClose={closeUploadDrawer}
        onImport={handleImport}
      />

      <ReviewSkuDrawer
        open={reviewCode !== null}
        sku={reviewingSku}
        subcats={subcats}
        packs={packs}
        onClose={closeReviewDrawer}
        onSave={handleSaveReview}
        onNavigate={onNavigate}
      />

      <FixCodeDrawer
        open={fixCode !== null}
        sku={fixingSku}
        skus={state.skus}
        onClose={closeFixCodeDrawer}
        onSave={handleSaveCode}
      />
    </Box>
  )
}
